import { Fragment, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import {
  makeStyles,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tag,
  TagGroup,
  tokens,
  typographyStyles,
} from "@fluentui/react-components";

import escapeRegExp from "@aglocal/web/helpers/escapeRegExp";
import hasName from "@aglocal/web/helpers/hasName";
import UniqueList from "@aglocal/web/helpers/UniqueList";

import type {
  DarkquellInferredSkill,
  DarkquellSkill,
} from "@aglocal/schema/Darkquell";
import type { Interview } from "@aglocal/schema/Interview";
import type {
  Transcript as TTranscript,
  TranscriptWord as TWord,
} from "@aglocal/schema/Transcript";

import Stack from "@aglocal/web/components/Stack";
import { Heading, Span } from "@aglocal/web/components/Typography";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalS,
  },
  interviewerContainer: {
    alignItems: "flex-start",
  },
  intervieweeContainer: {
    alignItems: "flex-end",
  },
  speaker: {
    ...typographyStyles.caption1,
    padding: `${tokens.spacingVerticalNone} ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground2,
  },
  text: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderRadius: "0.5rem",
    maxWidth: "90%",
  },
  interviewerText: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  intervieweeText: {
    backgroundColor: tokens.colorBrandBackground2,
    float: "right",
  },
  popover: {
    maxWidth: "36rem",
  },
  popoverHeader: {
    marginTop: 0,
    marginBottom: tokens.spacingVerticalS,
  },
});

// Soft palette for guest recruiters — deliberately avoids blue (talent) and grey
// (primary interviewer) so the talent stays visually unique on the right.
const GUEST_RECRUITER_COLORS = [
  tokens.colorPaletteLavenderBackground2,
  tokens.colorPaletteGreenBackground2,
  tokens.colorPalettePeachBackground2,
  tokens.colorPaletteTealBackground2,
  tokens.colorPalettePinkBackground2,
  tokens.colorPaletteMarigoldBackground2,
];

interface SearchPart {
  text: string;
  skills?: UniqueList<DarkquellSkill>;
}

class SkillIndex {
  private index = new Map<string, UniqueList<DarkquellSkill>>();
  private pattern: RegExp;

  constructor(skills: readonly DarkquellInferredSkill[]) {
    skills.forEach(({ skill, sources }) => {
      sources.forEach((source) => {
        let skills = this.index.get(source.text);
        if (skills == null) {
          skills = new UniqueList(undefined, ({ id }) => id);
          this.index.set(source.text, skills);
        }
        skills.add(skill);
      });
    });
    this.pattern = new RegExp(
      [...this.index.keys()].map(escapeRegExp).join("|"),
      "gi",
    );
  }

  search(value: string): SearchPart[] {
    const results: SearchPart[] = [];

    let position = 0;

    for (const match of value.matchAll(this.pattern)) {
      if (match.index > position) {
        results.push({ text: value.slice(position, match.index) });
      }

      results.push({ text: match[0], skills: this.index.get(match[0]) });
      position = match.index + match[0].length;
    }

    if (position < value.length) {
      results.push({ text: value.slice(position) });
    }

    return results;
  }
}

interface TextWithSkillsProps {
  text: string;
  skillIndex?: SkillIndex;
}

function TextWithSkills({ text, skillIndex }: TextWithSkillsProps) {
  const styles = useStyles();
  const parts = useMemo(
    () => (skillIndex ? skillIndex.search(text) : [{ text }]),
    [text, skillIndex],
  );

  return (
    <>
      {parts.map(({ text, skills }, index) =>
        skills?.length ? (
          <Popover key={index} openOnHover withArrow inline mouseLeaveDelay={0}>
            <PopoverTrigger disableButtonEnhancement>
              <span>
                {" "}
                <Span underline>{text}</Span>{" "}
              </span>
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
              <Heading
                as="h3"
                variant="subtitle2"
                className={styles.popoverHeader}
              >
                <FormattedMessage
                  defaultMessage="Skills found:"
                  description="Heading of the popover listing the skills detected in a transcript sentence."
                />
              </Heading>
              <TagGroup>
                {skills.map((skill) => (
                  <Tag key={skill.id}>{skill.name}</Tag>
                ))}
              </TagGroup>
            </PopoverSurface>
          </Popover>
        ) : (
          <Fragment key={index}>{text}</Fragment>
        ),
      )}
    </>
  );
}

type SpeakerVariant = "talent" | "primary" | "guest";

interface TranscriptBlock {
  speaker: string | null;
  text: string;
  variant: SpeakerVariant;
  // Inline background colour, only set for guest recruiters.
  color?: string;
}

interface TranscriptItemProps extends TranscriptBlock {
  skillIndex?: SkillIndex;
}

function TranscriptItem({
  variant,
  color,
  speaker,
  text,
  skillIndex,
}: TranscriptItemProps) {
  const styles = useStyles();
  const isTalent = variant === "talent";

  return (
    <div
      className={mergeClasses(
        styles.container,
        isTalent ? styles.intervieweeContainer : styles.interviewerContainer,
      )}
    >
      {speaker && <div className={styles.speaker}>{speaker}</div>}
      <div
        className={mergeClasses(
          styles.text,
          isTalent ? styles.intervieweeText : styles.interviewerText,
        )}
        style={variant === "guest" ? { backgroundColor: color } : undefined}
      >
        <TextWithSkills text={text} skillIndex={skillIndex} />
      </div>
    </div>
  );
}

// Positive talent gate: only the confirmed talent renders on the right in blue. Prefers
// speaker_id (200 talent / 100 recruiter), falling back to a name match when absent.
function isTalentSpeaker(
  speakerId: number | null | undefined,
  speaker: string | null,
  interview: Interview,
): boolean {
  if (speakerId === 200) return true;
  if (speakerId != null) return false;
  return interview.interviewee?.displayName && speaker
    ? hasName(speaker, interview.interviewee.displayName)
    : false;
}

function isPrimaryInterviewer(
  speaker: string | null,
  interview: Interview,
): boolean {
  return interview.interviewer?.displayName && speaker
    ? hasName(speaker, interview.interviewer.displayName)
    : false;
}

export interface TranscriptProps {
  interview: Interview;
  transcript: TTranscript;
  skills?: readonly DarkquellInferredSkill[];
}

export default function Transcript({
  interview,
  transcript,
  skills,
}: TranscriptProps) {
  const skillIndex = useMemo(() => skills && new SkillIndex(skills), [skills]);
  const formatted = useMemo<TranscriptBlock[]>(() => {
    const combined: {
      speaker?: string | null;
      speakerId?: number | null;
      words: TWord[];
    }[] = [];

    for (const { speaker, speaker_id, words = [] } of transcript) {
      const previousSpeaker = combined.length
        ? combined[combined.length - 1].speaker
        : null;

      if (previousSpeaker == null || previousSpeaker !== speaker) {
        combined.push({ speaker, speakerId: speaker_id, words: [] });
      }

      combined[combined.length - 1].words.push(...words);
    }

    // Assign each distinct guest recruiter a stable colour from the palette, in
    // order of first appearance, so the same person keeps the same colour.
    const guestColors = new Map<string, number>();

    return combined.map(({ speaker = null, speakerId = null, words }) => {
      const text = words.map(({ text }) => text).join(" ");

      if (isTalentSpeaker(speakerId, speaker, interview)) {
        return { speaker, text, variant: "talent" as const };
      }

      if (speaker == null || isPrimaryInterviewer(speaker, interview)) {
        return { speaker, text, variant: "primary" as const };
      }

      let index = guestColors.get(speaker);
      if (index == null) {
        index = guestColors.size;
        guestColors.set(speaker, index);
      }

      return {
        speaker,
        text,
        variant: "guest" as const,
        color: GUEST_RECRUITER_COLORS[index % GUEST_RECRUITER_COLORS.length],
      };
    });
  }, [interview, transcript]);
  return (
    <Stack gap="s">
      {formatted.map((item, index) => (
        <TranscriptItem key={index} skillIndex={skillIndex} {...item} />
      ))}
    </Stack>
  );
}
